import { IToggle } from '@unleash/nextjs';

import { getGenres } from 'src/components/pageMeta/SEOMetaUtils';
import { ContentTypes, ThumbnailTypes } from 'src/components/thumbnail/types';
import {
  ConsumptionPageTypes,
  PersonalizedContent,
  ContentLiteData
} from 'src/types/consumption';
import { isServer } from 'src/utils/common';
import { DynamicObject } from 'src/utils/commonTypes';
import {
  API_LIMIT,
  ASSET_SUBTYPE,
  DEVICE_PLATFORM
} from 'src/utils/constants/AppConstants';
import {
  API_STATUS_TYPES,
  FeatureFlagConstants
} from 'src/utils/constants/utilConstants';
import { LANGUAGE_LABELS } from 'src/utils/languageConstants';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';
import { titleGenerator } from 'src/utils/routeHandler';
import { TAG_NAME } from '../../utils/constants/TagNameConstants';
import { fetchEpisodes } from '../apiRequests/episodeConsumptionPage/fetchEpisodesHandler';
import { fetchStories } from '../apiRequests/episodeConsumptionPage/fetchStoriesHandler';
import {
  fetchTvShowRelatedContents,
  fetchTvShowRelatedContentsForBot
} from '../apiRequests/episodeConsumptionPage/fetchTvshowRelatedContentsHandler';
import { getRecommendedContents } from '../apiRequests/episodeConsumptionPage/fetchUpnextEpisodesHandler';
import { fetchCollection } from '../apiRequests/MovieConsumptionPage/fetchCollection';
import { fetchLatestMovies } from '../apiRequests/MovieConsumptionPage/FetchLatestMovies';
import { fetchMusic } from '../apiRequests/MovieConsumptionPage/fetchMusic';
import { fetchPersonalisedContent } from '../apiRequests/MovieConsumptionPage/fetchPersonalisedContent';
import { fetchRailsByActors } from '../apiRequests/MovieConsumptionPage/fetchRailsByActors';
import { fetchSimilarContents } from '../apiRequests/MovieConsumptionPage/fetchSimilarContents';
import { moviesByActorType } from '../responseTypes/moviesByActorsResponse';
import { CONSUMPTION_PAGE_STRING_CONSTANTS } from '../../utils/constants/utilConstants';
import { Collection } from '../GraphQL/Queries/types';
import {
  fetchSimilarContentGenre,
  fetchRecommendedContents
} from '../apiRequests/MovieConsumptionPage/fetchRecommendedContents';

export interface IRails {
  fetchRecommendedContents?(): Promise<any>;
  fetchEpisodeContents?(page: number, platformToken: string): Promise<any>;
  fetchSimilarContents(isPersonalisedEnabled?: boolean): Promise<any>;
  fetchSimilarContentsForBot?(): Promise<any>;
  fetchCollection?: (
    collectionId: string,
    contentInstance?: any
  ) => Promise<any>;
  fetchOtherContents?: () => Promise<any>;
  fetchStoriesContents?: () => Promise<any>;
}
type Translations = { [key: string]: DynamicObject } | null;

class MovieRails implements IRails {
  private requestObj: ConsumptionPageTypes.MovieRailsRequestObject;
  private translations: Translations;
  constructor(
    requestObj: ConsumptionPageTypes.MovieRailsRequestObject,
    translations: Translations
  ) {
    this.requestObj = requestObj;
    this.translations = translations;
  }

  async fetchRecommendedContents(): Promise<any> {
    const {
      translation,
      region,
      languages,
      country,
      locale,
      contentId,
      assetSubType,
      contentGenre
    } = this.requestObj;
    const genres = getGenres(contentGenre);
    const recommendedContents = await fetchRecommendedContents(
      contentId,
      country,
      translation,
      region,
      languages
    );
    if (recommendedContents?.length) {
      return {
        recommendedContentData: recommendedContents,
        thumbnailType: ThumbnailTypes.vertical
      };
    } else {
      const similarContents = await fetchSimilarContentGenre(
        assetSubType || '',
        genres,
        languages,
        country,
        translation,
        contentId
      );
      const similarContentsData = [
        {}, // Indicates Movies You May Like rail
        {
          contents: similarContents.items || [],
          title: similarContents.title
        } // Indicates Recommended Contents rail
      ];
      return {
        recommendedContentData: similarContentsData,
        thumbnailType: ThumbnailTypes.vertical
      };
    }
  }

  async fetchCollection(
    collectionId: string,
    contentInstance: Rails
  ): Promise<any> {
    const { translation, languages, country } = this.requestObj;
    const isPersonalisedCollection =
      contentInstance?.similarContentsRes?.relatedCollections?.collections.find(
        (item: Collection) =>
          item.id === collectionId &&
          item.assetSubType === CONSUMPTION_PAGE_STRING_CONSTANTS.PERSONALIZED
      );
    if (isPersonalisedCollection) {
      return {
        ...isPersonalisedCollection,
        rails: [{ contents: isPersonalisedCollection.contents }]
      };
    }
    return fetchCollection(collectionId, country, translation, languages);
  }
  async fetchSimilarContents(isPersonalisedEnabled = false): Promise<any> {
    const { translation, country, contentId, isRequestedByBot, languages } =
      this.requestObj;
    return await fetchSimilarContents(
      contentId,
      country,
      languages,
      translation,
      isPersonalisedEnabled,
      isServer() && isRequestedByBot
    );
  }

  // eslint-disable-next-line cyclomatic-complexity/zee-codeBlockComplexity
  async fetchOtherContents(): Promise<any> {
    const railsArray = [];
    const {
      contentId,
      country,
      translation,
      contentTitle,
      contentGenre,
      contentSupportedLanguages,
      languages,
      isRequestedByBot,
      isServer,
      requestedFromClient,
      actors
    } = this.requestObj;

    const consumptionPageTranslations = this.translations!.consumption;

    const {
      collection_title_movie_trailers,
      collection_title_latest_movies,
      collection_title_movie_music_videos,
      LANGUAGES,
      collection_title_movies
    } = consumptionPageTranslations;
    if (isRequestedByBot && !isServer) return [];
    // SEO Trailer rail
    const movieTrailerDataItems = await fetchMusic(
      contentId,
      country,
      translation,
      'trailer'
    );
    if (movieTrailerDataItems) {
      const railsData = {
        title: collection_title_movie_trailers.replace(
          '{{MovieName}}',
          contentTitle
        ),
        tagName: ['news_title'],
        moreLink: '',
        contents: movieTrailerDataItems
      };
      railsArray.push(railsData);
    }
    if (
      (isRequestedByBot && isServer) ||
      (isRequestedByBot && requestedFromClient)
    ) {
      const movieLanguageDataItems = await fetchLatestMovies(
        translation,
        contentSupportedLanguages[0],
        country
      );
      if (movieLanguageDataItems) {
        const railsData = {
          title: `${
            LANGUAGES[contentSupportedLanguages[0]] +
            ' ' +
            collection_title_movies
          }`,
          moreLink: `/movies/lang/${LANGUAGE_LABELS.en[
            contentSupportedLanguages[0] as keyof typeof LANGUAGE_LABELS
          ]?.toLowerCase()}`,
          contents: movieLanguageDataItems
        };
        railsArray.push(railsData);
      }
      return railsArray;
    }
    // SEO Music video rail
    const movieMusicVideoDataItems = await fetchMusic(
      contentId,
      country,
      translation,
      'music'
    );
    if (movieMusicVideoDataItems) {
      const railsData = {
        title: collection_title_movie_music_videos.replace(
          '{{MovieName}}',
          contentTitle
        ),
        tagName: movieMusicVideoDataItems.tags || [
          TAG_NAME.DIFFERENT_EPISODE_LIST
        ],
        moreLink: '',
        contents: movieMusicVideoDataItems
      };
      railsArray.push(railsData);
    }
    // SEO Latest rail
    const latestMovieDataItems = await fetchLatestMovies(
      translation,
      languages,
      country
    );
    if (latestMovieDataItems) {
      const railsData = {
        title: collection_title_latest_movies,
        moreLink: `/movies/new-movies`,
        contents: latestMovieDataItems
      };
      railsArray.push(railsData);
    }
    // SEO Language rail
    const movieLanguageDataItems = await fetchLatestMovies(
      translation,
      contentSupportedLanguages[0],
      country
    );
    if (movieLanguageDataItems) {
      const railsData = {
        title: `${
          LANGUAGES[contentSupportedLanguages[0]] +
          ' ' +
          collection_title_movies
        }`,
        moreLink: `/movies/lang/${LANGUAGE_LABELS.en[
          contentSupportedLanguages[0] as keyof typeof LANGUAGE_LABELS
        ]?.toLowerCase()}`,
        contents: movieLanguageDataItems
      };
      railsArray.push(railsData);
    }
    // SEO Genre rail
    const movieGenreDataItems = await fetchLatestMovies(
      translation,
      languages,
      country,
      [contentGenre[0].value]
    );
    if (movieGenreDataItems) {
      const railsData = {
        title: `${[contentGenre[0].value] + ' ' + collection_title_movies}`,
        moreLink: `/movies/genre/${contentGenre[0].value.toLowerCase()}`,
        contents: movieGenreDataItems
      };
      railsArray.push(railsData);
    }
    // SEO Actors rail
    /**
     * 2nd Nov23, adding addition check of process.env.NEXT_PUBLIC_SHOULD_SHOW_ACTORS_RAIL.
     * As per product requirement we no longer need Actors rail on consumption page.
     *
     * We are disabling it using env for now.
     */
    const shouldShowActorsRail = JSON.parse(
      `${process.env.NEXT_PUBLIC_SHOULD_SHOW_ACTORS_RAIL}`
    );
    if (shouldShowActorsRail) {
      if (actors && actors !== 'null' && actors !== 'undefined') {
        const actorsDataItems = await fetchRailsByActors(this.requestObj);
        if (actorsDataItems?.length) {
          actorsDataItems.forEach((actorData: moviesByActorType) => {
            const railsData = {
              title: `${actorData.title + ' ' + collection_title_movies}`,
              moreLink: `/movies/celeb/${titleGenerator(actorData.title)}`,
              contents: actorData.contents
            };
            railsArray.push(railsData);
          });
        }
      }
    }

    return railsArray;
  }
}

class TVShowRails implements IRails {
  private requestObj: ConsumptionPageTypes.TVShowRailsRequestObject;
  private translations: Translations;
  constructor(
    requestObj: ConsumptionPageTypes.TVShowRailsRequestObject,
    translations: Translations
  ) {
    this.requestObj = requestObj;
    this.translations = translations;
  }
  fetchSimilarContentsForBot(): Promise<any> {
    const { country, translation, showId, languages } = this.requestObj;
    return fetchTvShowRelatedContentsForBot(
      showId,
      country,
      translation,
      languages,
      DEVICE_PLATFORM.MOBILE_WEB
    );
  }

  async fetchRecommendedContents() {
    const {
      episodeId,
      seasonId,
      allLang,
      translation,
      contentGenre,
      contentSupportedLanguages
    } = this.requestObj;
    const contentLanguage =
      (contentSupportedLanguages?.length && contentSupportedLanguages[0]) ||
      translation;
    const genres =
      contentGenre?.map((item) => item?.value?.toLowerCase()) || [];
    const reqObj = {
      episodeId,
      seasonId,
      contentLanguage,
      type: 'tvshow',
      genres,
      allLang,
      translationsData: this.translations!.consumption
    };
    return await getRecommendedContents(reqObj);
  }
  fetchSimilarContents(): Promise<any> {
    const { country, translation, showId, languages } = this.requestObj;
    return fetchTvShowRelatedContents(showId, country, translation, languages);
  }
  fetchCollection(collectionId: string): Promise<any> {
    const { translation, languages, country } = this.requestObj;
    return fetchCollection(collectionId, country, translation, languages);
  }
  fetchEpisodeContents(page: number): Promise<any> {
    const { country, translation, seasonId, onAir } = this.requestObj;
    return fetchEpisodes(
      country,
      translation,
      seasonId,
      page,
      ASSET_SUBTYPE.EPISDOE,
      API_LIMIT.TEN,
      onAir
    );
  }
  fetchStoriesContents(): Promise<any> {
    const { showId = '' } = this.requestObj;
    return fetchStories(showId);
  }

  // Browse by Genre and Browse by Language rails will not shown in consumption pages

  // async fetchOtherContents(): Promise<any> {
  //   const railsArray = [];
  //   const {
  //     country: countryCode,
  //     translation,
  //     contentGenre,
  //     contentSupportedLanguages,
  //     allLang
  //   } = this.requestObj;
  //   // const consumptionPageTranslations = this.translations!.consumption;

  //   // SEO Language rail
  //   const tvshowLanguageDataItems = await fetchGenreOrLanguage(
  //     'tvshow',
  //     countryCode,
  //     'language'
  //   );
  //   if (tvshowLanguageDataItems) {
  //     const railsData = {
  //       title: `${STRING_CONSTANTS.BROWSE_BY_LANGUAGE}`,
  //       contents: tvshowLanguageDataItems,
  //       tagName: TAG_NAME.EPISODES
  //     };
  //     railsArray.push(railsData);
  //   }

  //   // SEO Genre rail
  //   const tvshowGenreDataItems = await fetchGenreOrLanguage(
  //     'tvshow',
  //     countryCode,
  //     'genre'
  //   );
  //   if (tvshowGenreDataItems) {
  //     const railsData = {
  //       title: `${STRING_CONSTANTS.BROWSE_BY_GENRE}`,
  //       contents: tvshowGenreDataItems,
  //       tagName: TAG_NAME.EPISODES
  //     };
  //     railsArray.push(railsData);
  //   }

  //   return railsArray;
  // }
}

// Discussed with Amit this any type will be removed automatically when fuctional component implementation will come
export class Rails {
  public contentInstance: any;
  public similarContentsRes: any;
  public recommendedContentsResponse: any;

  static getContentRailInstance(
    type: ContentTypes,
    requestObj: ConsumptionPageTypes.SupportedRequestObjectsForRails,
    translations: { [key: string]: DynamicObject } | null
  ) {
    switch (type) {
      case ContentTypes.movies: {
        return new Rails(
          new MovieRails(
            requestObj as ConsumptionPageTypes.MovieRailsRequestObject,
            translations
          )
        );
      }
      case ContentTypes.tvshows:
      case ContentTypes.webSeries: {
        return new Rails(
          new TVShowRails(
            requestObj as ConsumptionPageTypes.TVShowRailsRequestObject,
            translations
          )
        );
      }
      default:
        return new Rails(
          new MovieRails(
            requestObj as ConsumptionPageTypes.MovieRailsRequestObject,
            translations
          )
        );
    }
  }
  // Discussed with Amit this any type will be removed automatically when fuctional component implementation will come
  constructor(contentInstance: any) {
    this.contentInstance = contentInstance;
    this.similarContentsRes = null;
    this.recommendedContentsResponse = null;
  }
  // Discussed with Amit this any type will be removed automatically when fuctional component implementation will come
  async fetchRailContents(remoteConfig: IToggle[]) {
    const seoConsumptionRailsEnabled = isFeatureEnabled(
      remoteConfig,
      FeatureFlagConstants.FEATURE_SEO_CONSUMPTION_RAILS
    );
    const customFeaturingRailsEnabled = isFeatureEnabled(
      remoteConfig,
      FeatureFlagConstants.FEATURE_CUSTOM_RAIL_FEATURING
    );

    let otherContents = [];
    let storiesContents = [];
    let recommendedContentsResponse: any = [];
    let similarContentsResponse: any = [];

    const isMovieAsset =
      String(this.contentInstance?.requestObj?.assetType) === '0';
    if (isMovieAsset && customFeaturingRailsEnabled) {
      ({ similarContentsResponse, recommendedContentsResponse } =
        await this.handleMovieAsset());
    } else {
      ({ similarContentsResponse, recommendedContentsResponse } =
        await this.handleNonMovieAsset());
    }

    if (this.contentInstance.fetchOtherContents && seoConsumptionRailsEnabled) {
      otherContents = await this.contentInstance.fetchOtherContents();
    }

    if (this.contentInstance.fetchStoriesContents) {
      storiesContents = await this.contentInstance.fetchStoriesContents();
    }

    this.similarContentsRes = similarContentsResponse || null;

    return {
      similarContents: similarContentsResponse || null,
      recommendedContents: recommendedContentsResponse,
      otherContents,
      storiesContents
    };
  }

  async handleMovieAsset() {
    const { translation, languages, country, contentId } =
      this.contentInstance.requestObj;
    const similarAPIResponse = await this.contentInstance.fetchSimilarContents(
      true
    );
    let similarContentsResponse = JSON.parse(
      JSON.stringify(similarAPIResponse)
    );
    let recommendedContentsResponse: any = {};

    const collectionList = similarAPIResponse?.relatedCollections?.collections;
    const collectionLength = collectionList?.length || 0;

    for (let index = 0; index < collectionLength; index++) {
      const collection = collectionList[index];
      const extendedNode = collection?.extended;
      const personalizationType = extendedNode?.personalizationType;
      const personalizationMetaData = extendedNode?.personalizationMetaData;

      if (
        collection?.assetSubType ===
          CONSUMPTION_PAGE_STRING_CONSTANTS.PERSONALIZED &&
        index === 0
      ) {
        ({ recommendedContentsResponse, similarContentsResponse } =
          await this.fetchPersonalizedContent(
            personalizationType,
            personalizationMetaData,
            contentId,
            country,
            languages,
            translation,
            similarAPIResponse
          ));
        break;
      }

      if (index === 0 && this.contentInstance?.fetchCollection) {
        const response = await this.contentInstance.fetchCollection(
          collection?.id,
          this.contentInstance
        );
        recommendedContentsResponse = this.createRecommendedResponse(response);
      }
    }

    recommendedContentsResponse = {
      recommendedContentData: [{}, recommendedContentsResponse],
      thumbnailType: ThumbnailTypes.vertical
    };
    similarContentsResponse.relatedCollections.collections.shift();

    return { similarContentsResponse, recommendedContentsResponse };
  }

  // If Feature Flag -> FEATURE_CUSTOM_RAIL_FEATURING = false it will connect the journey here for Movie asset as well
  async handleNonMovieAsset() {
    const [similarContentsResponse, recommendedContentsResponse]: any =
      await Promise.allSettled([
        this.contentInstance.fetchSimilarContents(false),
        this.contentInstance?.fetchRecommendedContents &&
          this.contentInstance.fetchRecommendedContents()
      ]);
    return {
      similarContentsResponse: similarContentsResponse?.value,
      recommendedContentsResponse: recommendedContentsResponse?.value
    };
  }

  async fetchPersonalizedContent(
    personalizationType: string,
    personalizationMetaData: string,
    contentId: string,
    country: string,
    languages: string,
    translation: string,
    similarAPIResponse: any
  ) {
    const personalizationParams = {
      personalizationType: personalizationType || '',
      personalizationMetaData: personalizationMetaData || '',
      sourceType: CONSUMPTION_PAGE_STRING_CONSTANTS.CONSUMPTIONPAGE,
      contentId: contentId,
      country: country || '',
      languages: languages || '',
      translation: translation || ''
    };

    const personalizedResponse: PersonalizedContent =
      await fetchPersonalisedContent(personalizationParams);

    const recommendedContentsResponse =
      personalizedResponse?.personalizedRails?.[0] || {};
    if (
      personalizedResponse &&
      personalizedResponse?.personalizedRails?.length
    ) {
      const updatedCollections = [
        ...personalizedResponse.personalizedRails.slice(1),
        ...similarAPIResponse.relatedCollections.collections
      ];
      similarAPIResponse.relatedCollections.collections = updatedCollections;
    }

    return {
      recommendedContentsResponse,
      similarContentsResponse: similarAPIResponse
    };
  }

  createRecommendedResponse(response: any) {
    return {
      ...response?.rails?.[0],
      title: response?.title,
      originalTitle: response?.originalTitle
    };
  }
}

export const getBotRailsData = async (
  contentLiteData: ContentLiteData,
  reqObj: any,
  isBot: boolean,
  translations: any,
  remoteConfig: IToggle[]
) => {
  const seoBotsConsumptionRailsEnabled =
    isFeatureEnabled(
      remoteConfig,
      FeatureFlagConstants.FEATURE_SEO_CONSUMPTION_RAILS
    ) || false;

  let railsData: any = {
    otherCollection: []
  };
  if (!isBot || !seoBotsConsumptionRailsEnabled) return railsData;
  const { genres: contentGenres } = contentLiteData;
  const getActorsFromArray = contentLiteData?.actors
    ?.map((str) => str?.split(':')[0])
    .join(',');
  railsData.status = API_STATUS_TYPES.loading;

  const requestObj = {
    contentId: contentLiteData.id,
    contentTitle: contentLiteData.title,
    country: reqObj.countryCode,
    languages: reqObj.languages,
    translation: reqObj.translation as string,
    region: reqObj.region as string,
    contentSupportedLanguages: contentLiteData.languages,
    contentGenre: contentGenres as any[],
    actors: getActorsFromArray as string,
    isRequestedByBot: isBot,
    isServer: true
  };
  const contentInstance: any = Rails.getContentRailInstance(
    ContentTypes.movies,
    requestObj,
    translations
  );

  try {
    if (seoBotsConsumptionRailsEnabled) {
      const otherContents =
        await contentInstance.contentInstance.fetchOtherContents();
      railsData = {
        otherCollection: otherContents,
        status: API_STATUS_TYPES.success
      };
    }
  } catch (error) {
    railsData.status = API_STATUS_TYPES.failure;
  }
  return railsData;
};
